import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getSIVList, createSIV, deleteSIV, getItems } from '../../services/api';
import { FiPlus, FiTrash2, FiX, FiUpload } from 'react-icons/fi';
import Toast from '../../components/Toast';

const SIV = () => {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [items, setItems] = useState([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), issued_to: '', purpose: '' });
  const [lines, setLines] = useState([{ item_id: '', quantity: '' }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }));

  const fetchList = () => getSIVList({ from, to }).then(r => setList(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  const fetchItems = () => getItems().then(r => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => {});

  useEffect(() => { fetchList(); }, [from, to]);
  useEffect(() => { fetchItems(); }, []);

  const setLine = (i, field, value) => setLines(prev => { const u = [...prev]; u[i] = { ...u[i], [field]: value }; return u; });
  const addLine = () => setLines(p => [...p, { item_id: '', quantity: '' }]);
  const removeLine = (i) => setLines(p => p.filter((_, idx) => idx !== i));

  const handleAdd = async () => {
    setError('');
    if (!form.date || !form.issued_to) return setError('Date and issued to are required');
    const validLines = lines.filter(l => l.item_id && l.quantity);
    if (validLines.length === 0) return setError('Add at least one item');
    setSaving(true);
    try {
      await createSIV({ ...form, items: validLines });
      setShowAdd(false);
      setForm({ date: new Date().toISOString().slice(0, 10), issued_to: '', purpose: '' });
      setLines([{ item_id: '', quantity: '' }]);
      showToast('SIV created');
      fetchList();
      fetchItems();
    } catch (e) { setError(e.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete ${r.siv_number}? Stock will be restored.`)) return;
    await deleteSIV(r.id);
    showToast('Deleted', 'error');
    fetchList();
    fetchItems();
  };

  const getItemStock = (item_id) => {
    const item = items.find(i => String(i.id) === String(item_id));
    return item ? parseFloat(item.current_stock) : null;
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h1>{t('nav.siv')}</h1><p>{list.length}</p></div>
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}><FiPlus /> {t('form.newSIV')}</button>
      </div>

      <div className="filters-row">
        <label style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>{t('table.from')}</label>
        <input className="filter-input" type="date" value={from} onChange={e => setFrom(e.target.value)} />
        <label style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>{t('table.to')}</label>
        <input className="filter-input" type="date" value={to} onChange={e => setTo(e.target.value)} />
      </div>

      <div className="table-card">
        <table>
          <thead>
            <tr><th>#</th><th>SIV {t('finance.crNumber').replace('CR', 'SIV')}</th><th>{t('common.date')}</th><th>{t('table.issuedTo')}</th><th>{t('table.purpose')}</th><th>{t('table.items')}</th><th>{t('table.by')}</th><th></th></tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr><td colSpan={8}><div className="empty-state"><div className="empty-icon"><FiUpload /></div><p>{t('finance.noRecords')}</p></div></td></tr>
            ) : list.map((r, i) => (
              <tr key={r.id}>
                <td data-label="#">{i + 1}</td>
                <td data-label={`SIV ${t('finance.crNumber').replace('CR', 'SIV')}`}><strong>{r.siv_number}</strong></td>
                <td data-label={t('common.date')}>{r.date}</td>
                <td data-label={t('table.issuedTo')}>{r.issued_to}</td>
                <td data-label={t('table.purpose')}>{r.purpose || '—'}</td>
                <td data-label={t('table.items')}>{r.item_count}</td>
                <td data-label={t('table.by')}>{r.created_by_name}</td>
                <td data-label="Actions">
                  <button className="btn-icon danger" onClick={() => handleDelete(r)}><FiTrash2 /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 700 }}>
            <div className="modal-header">
              <div><h3>New Store Issue Voucher</h3></div>
              <button className="btn-icon" onClick={() => setShowAdd(false)}><FiX /></button>
            </div>
            <div className="modal-body">
              {error && <div className="error-msg">{error}</div>}
              <div className="form-grid" style={{ marginBottom: 20 }}>
                <div className="form-group"><label>Date <span className="required">*</span></label><input type="date" value={form.date} onChange={set('date')} /></div>
                <div className="form-group"><label>Issued To <span className="required">*</span></label><input value={form.issued_to} onChange={set('issued_to')} /></div>
                <div className="form-group form-full"><label>Purpose</label><input value={form.purpose} onChange={set('purpose')} /></div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <strong style={{ fontSize: 13 }}>Items to Issue</strong>
                <button className="btn btn-sm btn-secondary" onClick={addLine}>+ Add Row</button>
              </div>

              <table>
                <thead><tr><th>Item</th><th>Available Stock</th><th>Quantity</th><th></th></tr></thead>
                <tbody>
                  {lines.map((l, i) => {
                    const stock = getItemStock(l.item_id);
                    const overStock = l.quantity && stock !== null && parseFloat(l.quantity) > stock;
                    return (
                      <tr key={i}>
                        <td>
                          <select value={l.item_id} onChange={e => setLine(i, 'item_id', e.target.value)} style={{ width: '100%' }}>
                            <option value="">— Select item —</option>
                            {items.map(item => <option key={item.id} value={item.id}>{item.name} {item.unit ? `(${item.unit})` : ''}</option>)}
                          </select>
                        </td>
                        <td style={{ color: stock !== null && stock <= 0 ? '#dc2626' : '#16a34a', fontWeight: 600 }}>
                          {stock !== null ? stock.toFixed(2) : '—'}
                        </td>
                        <td>
                          <input type="number" step="0.01" value={l.quantity} onChange={e => setLine(i, 'quantity', e.target.value)} style={{ width: 100, borderColor: overStock ? '#dc2626' : undefined }} />
                          {overStock && <div style={{ color: '#dc2626', fontSize: 11, marginTop: 2 }}>Exceeds stock</div>}
                        </td>
                        <td><button className="btn-icon danger" onClick={() => removeLine(i)}><FiTrash2 /></button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAdd} disabled={saving}>{saving ? 'Saving...' : 'Save SIV'}</button>
            </div>
          </div>
        </div>
      )}
      <Toast message={toast?.msg} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
};

export default SIV;
